/* eslint-disable @typescript-eslint/no-explicit-any */
import { NEGOTIATED_TYPE } from "@/constants";
import { formatCurrency } from "@/lib/cost-util";
export type ProviderCost = {
  networkTag: string;
  address: string;
  specialityCode: string;
  distance: number;
  networkTier: string;
  provider: Provider;
  networkName: string;
  lon: number;
  networkType: string;
  einDetail: EinDetail;
  placeOfServices: PlaceOfService[];
  lat: number;
};

export type Provider = {
  specialityDetails: SpecialityDetail[];
  providerGroupId: string;
  phone: string;
  npi: string;
  fullName: string;
  typeCode: string;
};

export type SpecialityDetail = {
  code: string;
  isPrimary: boolean;
  description: string;
};

export type EinDetail = {
  name: string | null;
  id: string;
  type: string;
};

export type PlaceOfService = {
  totalRate: number | null;
  negotiateArrangement: string;
  services: Service[];
  type: string;
  bundleCodes: string | null;
};

export type Service = {
  rates: Rate[];
  billingClass: string;
};

export type Rate = {
  negotiatedType: string;
  billingCodeModifiers: string[];
  negotiatedRate: number | null;
  calculatedRate: number | null;
};

export class ProviderCostView {
  private providerCost: ProviderCost;
  constructor(providerCost: ProviderCost) {
    this.providerCost = providerCost;
  }

  public providerName() {
    return this.providerCost.provider.fullName;
  }
  public employerName() {
    return this.providerCost.einDetail.name;
  }
  public address() {
    return this.providerCost.address;
  }
  public distance() {
    return this.providerCost.distance;
  }
  public networkTag() {
    return this.providerCost.networkTag;
  }
  public networkName() {
    return this.providerCost.networkName;
  }
  public providerPhone() {
    return this.providerCost.provider.phone ?? null;
  }
  public specialityDetails() {
    return this.providerCost.provider.specialityDetails ?? [];
  }
  public defaultPlaceOfService() {
    return this.providerCost.placeOfServices[0].type ?? "";
  }
  public placeOfServices() {
    return this.providerCost.placeOfServices ?? [];
  }
  public placeOfServiceRates(services: Service[]) {
    if (
      !services ||
      services.length === 0 ||
      !services.every((item) => item && Array.isArray(item.rates))
    ) {
      console.error("provider cost not available.");
      return undefined;
    }

    const getYourEstimatedCost = () => {
      const negotiatedType = services[0]?.rates[0]?.negotiatedType;
      const numRates = Math.max(...services.map((item) => item?.rates.length));
      const sumsByIndex = Array(numRates).fill(0);
      for (let i = 0; i < numRates; i++) {
        for (const entry of services) {
          const rate = entry.rates[i]?.calculatedRate ?? 0;
          sumsByIndex[i] += rate;
        }
      }

      const minTotal = Math.min(...sumsByIndex);
      const maxTotal = Math.max(...sumsByIndex);
      const totalEstimatedRate =
        minTotal === maxTotal
          ? negotiatedType === NEGOTIATED_TYPE.PERCENTAGE
            ? `N/A`
            : formatCurrency(minTotal)
          : negotiatedType === NEGOTIATED_TYPE.PERCENTAGE
            ? `N/A`
            : `${formatCurrency(minTotal)} - ${formatCurrency(maxTotal)}`;
      return totalEstimatedRate;
    };

    const processRateInfo = (rates: Rate[], billingClass: string) => {
      if (rates.length > 0) {
        const negotiatedType = rates[0].negotiatedType;

        /** For contracted rate */
        const validContractedRates = rates
          ?.map((obj) => obj?.negotiatedRate)
          .filter((amount) => amount !== null); // Filter out null contracted amounts
        const minContracted: number =
          validContractedRates.length > 0
            ? Math.min(...validContractedRates)
            : Infinity;
        const maxContracted: number =
          validContractedRates.length > 0
            ? Math.max(...validContractedRates)
            : Infinity;

        const contracted =
          minContracted === Infinity
            ? undefined
            : minContracted === maxContracted
              ? negotiatedType === NEGOTIATED_TYPE.PERCENTAGE
                ? `${minContracted}%`
                : formatCurrency(minContracted)
              : negotiatedType === NEGOTIATED_TYPE.PERCENTAGE
                ? `${minContracted}% - ${maxContracted}%`
                : `${formatCurrency(minContracted)} - ${formatCurrency(maxContracted)}`;

        /** For estimated rate */
        const validEstimatedRates = rates
          ?.map((obj) => obj?.calculatedRate)
          .filter((amount) => amount !== null); // Filter out null estimated amounts
        const minEstimated: number =
          validEstimatedRates.length > 0
            ? Math.min(...validEstimatedRates)
            : Infinity;
        const maxEstimated: number =
          validEstimatedRates.length > 0
            ? Math.max(...validEstimatedRates)
            : Infinity;
        const estimated =
          minEstimated === Infinity
            ? undefined
            : minEstimated === maxEstimated
              ? negotiatedType === NEGOTIATED_TYPE.PERCENTAGE
                ? `N/A`
                : formatCurrency(minEstimated)
              : negotiatedType === NEGOTIATED_TYPE.PERCENTAGE
                ? `N/A`
                : `${formatCurrency(minEstimated)} - ${formatCurrency(maxEstimated)}`;

        /** For total rate */
        const totalRate =
          minEstimated === Infinity
            ? "N/A"
            : minEstimated === maxEstimated
              ? negotiatedType === NEGOTIATED_TYPE.PERCENTAGE
                ? `N/A`
                : formatCurrency(minEstimated)
              : negotiatedType === NEGOTIATED_TYPE.PERCENTAGE
                ? `N/A`
                : `${formatCurrency(minEstimated)} - ${formatCurrency(maxEstimated)}`;

        return {
          billingClass,
          negotiatedRate: {
            min: minContracted,
            max: maxContracted,
            // label: `Contracted amount for ${billingClass} ${
            //   minContracted === maxContracted
            //     ? `${contracted}.`
            //     : `fee may varies from ${contracted}`
            // }`,
            label:
              negotiatedType === NEGOTIATED_TYPE.PERCENTAGE
                ? `${contracted} Amount (percentage)`
                : `Contracted amount ${
                    minContracted === maxContracted
                      ? `${contracted}`
                      : `fee may varies from ${contracted}`
                  }`,
          },
          estimatedRate: {
            min: minEstimated,
            max: maxEstimated,
            label:
              negotiatedType === NEGOTIATED_TYPE.PERCENTAGE
                ? undefined
                : `Your estimated shared of ${billingClass} ${
                    minEstimated === maxEstimated
                      ? `${estimated}.`
                      : `fee may varies from ${estimated}.`
                  }`,
          },
          rate: totalRate,
        };
      }
    };

    const info: any = {};

    if (services.length > 0) {
      const facility = services.find(
        (service) => service.billingClass === "FACILITY",
      );
      if (facility) {
        const facilityRateInfo = processRateInfo(
          facility.rates,
          facility.billingClass.toLowerCase(),
        );
        info["facility"] = facilityRateInfo;
      }

      const professional = services.find(
        (service) => service.billingClass === "PROFESSIONAL",
      );
      if (professional) {
        const professionalRateInfo = processRateInfo(
          professional.rates,
          professional.billingClass.toLowerCase(),
        );
        info["professional"] = professionalRateInfo;
      }

      const estimatedTotal = {
        billingClass: "ESTIMATED TOTAL",
        rate: getYourEstimatedCost(),
      };
      info["estimatedTotal"] = estimatedTotal;
    }

    return info;
  }

  public overallProcedureCost() {
    const EXCLUDED_POS = ["PHARMACY", "OTHERS"];
    const negotiatedType =
      this.providerCost.placeOfServices[0].services[0].rates[0].negotiatedType;
    if (negotiatedType === NEGOTIATED_TYPE.PERCENTAGE) {
      return "N/A";
    } else {
      const calculatesRates = this.providerCost.placeOfServices
        .filter((pos) => !EXCLUDED_POS.includes(pos.type))
        .flatMap((pos) => {
          const facility = pos.services[0];
          const professional = pos.services[1];
          const facilityRates = facility.rates || [];
          const professionalRates = professional.rates || [];

          const maxLen = Math.max(
            facilityRates.length,
            professionalRates.length,
          );
          const result = [];

          for (let i = 0; i < maxLen; i++) {
            const facRate = facilityRates[i]?.calculatedRate ?? 0;
            const profRate = professionalRates[i]?.calculatedRate ?? 0;
            result.push(facRate + profRate);
          }

          return result;
        });

      if (calculatesRates.length === 0) {
        const contractedCost = this.providerCost.placeOfServices[0].totalRate;
        return `${formatCurrency(contractedCost)}`;
      } else {
        const estimatedMin = Math.min(...calculatesRates);
        const estimatedMax = Math.max(...calculatesRates);

        if (estimatedMin === estimatedMax) {
          return formatCurrency(estimatedMin);
        } else {
          return `${formatCurrency(estimatedMin)} - ${formatCurrency(estimatedMax)}`;
        }
      }
    }
  }
}
